import { useEffect, useState } from 'react';
import useSWR, { mutate } from 'swr';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Frame } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { get, post, del, ApiError } from '@/lib/api';
import { formatRelative } from '@/lib/time';
import { CATEGORIES, GENDERS, DECORATIONS, TIERS, ALL_SIZES, PRINT_POSITIONS, VIEW_KEYS, genProductConfigId, type ProductConfig } from '@/lib/product-config';
import { ProductVisualEditor } from './ProductVisualEditor';
import { storeTemplateHref, templateEditorHref } from '@/lib/template-editor-query';

type Props = {
  open: boolean;
  onClose: () => void;
  product: ProductConfig | null;
  mode?: 'view' | 'create';
  onDuplicate?: (seed: ProductConfig) => void;
};

// GET /api/products/:id/links — the Product-360 linkage view (routes/product-links.js).
type LinkDesign = { designTemplateId: string; position: string; garmentColor?: string; label?: string };
type LinksPayload = {
  id: string;
  baseProduct: {
    baseProductCreated?: boolean;
    shopifyProductId?: string;
    masterInventoryKey?: string;
    [k: string]: unknown;
  } | null;
  masterInventoryExists: boolean;
  personalization: { zoneCount: number; templateIds: string[]; lastSyncedAt: string | null; lastSyncedZonesHash: string | null };
  storeTemplates: { id: string; name: string; sport: string | null; kind?: string | null; active: boolean; styleEntry: { styleNumber: string; designs?: LinkDesign[] } }[];
};

function Row({ label, value }: { label: string; value?: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-3 border-b border-divider py-1.5 text-[12px] last:border-b-0">
      <span className="text-muted">{label}</span>
      <span className="text-right">{value ?? <span className="text-subtle">—</span>}</span>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] uppercase tracking-wide text-muted">{label}</span>
      {children}
    </label>
  );
}

function Section({ title, badge, children }: { title: string; badge?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="border-t border-divider pt-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="text-[11px] uppercase tracking-wide text-muted">{title}</h3>
        {badge}
      </div>
      {children}
    </div>
  );
}

// Draft/Synced/Off from the server-owned sync state. The client can't compute
// the blob hash, so "Synced" means "a clean push landed at <date>" — the
// Assign page tracks edited-since-sync within an editing session.
function persBadge(p: LinksPayload['personalization']) {
  if (p.zoneCount === 0 && p.templateIds.length === 0) return <Badge className="bg-bg text-muted">Off</Badge>;
  if (p.lastSyncedAt && p.lastSyncedZonesHash) {
    return <Badge className="bg-success/10 text-success">Synced {formatRelative(p.lastSyncedAt)}</Badge>;
  }
  if (p.zoneCount > 0) return <Badge className="bg-warning/10 text-warning">Draft — not live</Badge>;
  return <Badge className="bg-warning/10 text-warning">legacy</Badge>;
}

const inputCls = 'w-full rounded-md border border-border bg-bg px-2 py-1.5 text-[12px]';

export function ProductConfigDrawer({ open, onClose, product, mode = 'view', onDuplicate }: Props) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<ProductConfig | null>(product);
  // Tags edit as free text (comma-split on save) — splitting per keystroke
  // would eat the separator the user is mid-typing.
  const [tagsText, setTagsText] = useState('');
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [linkBusy, setLinkBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [visualOpen, setVisualOpen] = useState(false);

  // Reset draft + exit edit mode whenever a different product opens.
  useEffect(() => {
    setDraft(product);
    setTagsText((product?.shopify?.tags || []).join(', '));
    setEditing(mode === 'create');
  }, [product, mode]);

  // Product-360 linkage — persisted configs only (create-mode seeds have no id).
  const linksKey = open && mode === 'view' && product?.id
    ? `/api/products/${encodeURIComponent(product.id)}/links`
    : null;
  const { data: links, error: linksError, mutate: mutateLinks } = useSWR<LinksPayload>(linksKey, get);

  if (!product || !draft) {
    return (
      <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
        <SheetContent className="w-[440px] sm:max-w-[440px]" />
      </Sheet>
    );
  }

  function set<K extends keyof ProductConfig>(key: K, value: ProductConfig[K]) {
    setDraft((d) => (d ? { ...d, [key]: value } : d));
  }
  // Patch one field inside a nested map (baseProduct, pricing, printArea, …).
  function patchMap(key: keyof ProductConfig, field: string, value: unknown) {
    setDraft((d) => (d ? { ...d, [key]: { ...((d[key] as Record<string, unknown>) || {}), [field]: value } } : d));
  }
  // Number inputs: '' clears the field; non-numeric garbage is dropped.
  function num(raw: string): number | undefined {
    if (raw.trim() === '') return undefined;
    const n = Number(raw);
    return Number.isFinite(n) ? n : undefined;
  }
  function toggleSize(size: string) {
    setDraft((d) => {
      if (!d) return d;
      const sizes = d.sizes || [];
      return { ...d, sizes: sizes.includes(size) ? sizes.filter((s) => s !== size) : [...sizes, size] };
    });
  }

  const styleOk = !!(draft.baseProduct?.styleNumber && String(draft.baseProduct.styleNumber).trim());
  // Decoration reads fall back to the legacy top-level fields still common in
  // older docs; saves write the canonical map plus the legacy mirrors.
  const deco = {
    method: draft.decoration?.method ?? draft.decorationMethod ?? 'dtf',
    detail: draft.decoration?.detail ?? draft.decorationDetail ?? '',
    leadTime: draft.decoration?.leadTime ?? draft.leadTimeDays,
  };

  async function save() {
    if (!draft) return;
    setSaving(true);
    try {
      const decoration = { method: deco.method, detail: deco.detail, leadTime: deco.leadTime ?? 10 };
      const tags = tagsText.split(/[,;]/).map((t) => t.trim()).filter(Boolean);
      const payload: ProductConfig = {
        ...draft,
        decoration,
        decorationMethod: decoration.method,
        decorationDetail: decoration.detail,
        leadTimeDays: decoration.leadTime,
        shopify: { ...(draft.shopify || {}), tags },
        id: mode === 'create' && !draft.id ? genProductConfigId(draft) : draft.id,
      };
      await post('/api/product-configs', { product: payload });
      toast.success(mode === 'create' ? 'Product created' : 'Product saved');
      setEditing(false);
      await mutate('/api/products?limit=500');
      onClose();
    } catch (e) {
      const body = (e as ApiError).body as { message?: string; error?: string } | null;
      toast.error(body?.message || body?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function toggleApprove() {
    if (!draft) return;
    setBusy(true);
    try {
      const next = { ...draft, approved: !draft.approved };
      await post('/api/product-configs', { product: next });
      setDraft(next);
      toast.success(next.approved ? 'Approved' : 'Unapproved');
      await mutate('/api/products?limit=500');
    } catch (e) {
      const body = (e as ApiError).body as { message?: string; error?: string } | null;
      toast.error(body?.message || body?.error || 'Update failed');
    } finally {
      setBusy(false);
    }
  }

  // Creates a NEW Shopify base product + masterInventory doc for this config —
  // the only Shopify write here, and it never touches existing store products.
  async function createBaseProduct() {
    if (!draft) return;
    if (!confirm('Creates a NEW base product in Shopify for inventory tracking. Does not touch any live store products.')) return;
    setLinkBusy(true);
    try {
      await post(`/api/products/${encodeURIComponent(draft.id)}/create-base-product`, undefined);
      toast.success('Base product created');
      await Promise.all([mutateLinks(), mutate('/api/products?limit=500')]);
    } catch (e) {
      const body = (e as ApiError).body as { message?: string; error?: string } | null;
      toast.error(body?.message || body?.error || 'Create failed');
    } finally {
      setLinkBusy(false);
    }
  }

  // Reconciles baseProduct flags FROM masterInventory (read-side fix-up).
  async function refreshBaseStatus() {
    if (!draft) return;
    setLinkBusy(true);
    try {
      const r = await post<{ updated?: number }>(`/api/products/${encodeURIComponent(draft.id)}/sync-base-product-status`, undefined);
      toast.success(typeof r.updated === 'number' ? `Status refreshed — ${r.updated} config(s) updated` : 'Status refreshed');
      await Promise.all([mutateLinks(), mutate('/api/products?limit=500')]);
    } catch (e) {
      const body = (e as ApiError).body as { message?: string; error?: string } | null;
      toast.error(body?.message || body?.error || 'Refresh failed');
    } finally {
      setLinkBusy(false);
    }
  }

  async function doDelete() {
    if (!draft) return;
    setBusy(true);
    try {
      await del(`/api/product-configs/${encodeURIComponent(draft.id)}`);
      toast.success('Product deleted');
      await mutate('/api/products?limit=500');
      onClose();
    } catch (e) {
      const body = (e as ApiError).body as { message?: string; error?: string } | null;
      toast.error(body?.message || body?.error || 'Delete failed');
    } finally {
      setBusy(false);
      setConfirmDelete(false);
    }
  }

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o && !saving) onClose(); }}>
      <SheetContent className="w-[440px] overflow-y-auto sm:max-w-[440px]">
        <SheetHeader>
          <SheetTitle className="text-[15px]">
            {draft.displayName || draft.productTitle || draft.id}
          </SheetTitle>
        </SheetHeader>

        <div className="mt-4">
          <div className="mb-3 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              {draft.approved
                ? <Badge className="bg-success/10 text-success">approved</Badge>
                : <Badge className="bg-warning/10 text-warning">pending</Badge>}
            </div>
            {!editing && <Button size="sm" variant="secondary" onClick={() => setEditing(true)}>Edit</Button>}
          </div>

          {editing ? (
            <div className="space-y-4">
              <div className="space-y-3">
                <Field label="Display Name">
                  <input className={inputCls} value={draft.displayName || ''} onChange={(e) => set('displayName', e.target.value)} />
                </Field>
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Product Title">
                    <input className={inputCls} value={draft.productTitle || ''} onChange={(e) => set('productTitle', e.target.value)} />
                  </Field>
                  <Field label="Short Name">
                    <input className={inputCls} value={draft.shortName || ''} onChange={(e) => set('shortName', e.target.value)} />
                  </Field>
                </div>
                <Field label="Description">
                  <textarea className={inputCls} rows={2} value={draft.description || ''} onChange={(e) => set('description', e.target.value)} />
                </Field>
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Category">
                    <select className={inputCls} value={draft.category || 'tees'} onChange={(e) => set('category', e.target.value)}>
                      {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </Field>
                  <Field label="Gender">
                    <select className={inputCls} value={draft.gender || 'unisex'} onChange={(e) => set('gender', e.target.value)}>
                      {GENDERS.map((g) => <option key={g} value={g}>{g}</option>)}
                    </select>
                  </Field>
                </div>
              </div>

              <Section title="Tier & brand">
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Tier">
                    <select className={inputCls} value={draft.tier || 'name-brand'} onChange={(e) => set('tier', e.target.value)}>
                      {TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </Field>
                  <Field label="Tier Label">
                    <input className={inputCls} value={draft.tierLabel || ''} onChange={(e) => set('tierLabel', e.target.value)} />
                  </Field>
                  <Field label="Brand Partner">
                    <input className={inputCls} value={draft.brandPartner || ''} onChange={(e) => set('brandPartner', e.target.value)} />
                  </Field>
                  <Field label="Sort Order">
                    <input className={inputCls} type="number" value={draft.sortOrder ?? ''} onChange={(e) => set('sortOrder', num(e.target.value))} />
                  </Field>
                </div>
                <label className="mt-2 flex items-center gap-2 text-[12px]">
                  <input type="checkbox" checked={draft.active !== false} onChange={(e) => set('active', e.target.checked)} />
                  Active — included when stores are generated
                </label>
              </Section>

              <Section title="Base product">
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Style Number *">
                    <input className={inputCls} value={draft.baseProduct?.styleNumber || ''} onChange={(e) => patchMap('baseProduct', 'styleNumber', e.target.value)} />
                  </Field>
                  <Field label="Color">
                    <input className={inputCls} value={draft.baseProduct?.color || ''} onChange={(e) => patchMap('baseProduct', 'color', e.target.value)} />
                  </Field>
                  <Field label="Color Code">
                    <input className={inputCls} placeholder="BLK" value={draft.baseProduct?.colorCode || ''} onChange={(e) => patchMap('baseProduct', 'colorCode', e.target.value)} />
                  </Field>
                  <Field label="Supplier">
                    <input className={inputCls} placeholder="SanMar" value={draft.baseProduct?.supplier || ''} onChange={(e) => patchMap('baseProduct', 'supplier', e.target.value)} />
                  </Field>
                  <Field label="Brand">
                    <input className={inputCls} value={draft.baseProduct?.brand || ''} onChange={(e) => patchMap('baseProduct', 'brand', e.target.value)} />
                  </Field>
                  <Field label="Fabric">
                    <input className={inputCls} placeholder="80% cotton, 20% poly" value={draft.baseProduct?.fabricComposition || ''} onChange={(e) => patchMap('baseProduct', 'fabricComposition', e.target.value)} />
                  </Field>
                </div>
              </Section>

              <Section title="Pricing">
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Base Price ($)">
                    <input className={inputCls} type="number" step="0.01" value={draft.pricing?.basePrice ?? ''} onChange={(e) => patchMap('pricing', 'basePrice', num(e.target.value))} />
                  </Field>
                  <Field label="Compare At ($)">
                    <input className={inputCls} type="number" step="0.01" value={draft.pricing?.compareAtPrice ?? ''} onChange={(e) => patchMap('pricing', 'compareAtPrice', num(e.target.value))} />
                  </Field>
                  <Field label="Cost ($)">
                    <input className={inputCls} type="number" step="0.01" value={draft.pricing?.cost ?? draft.pricing?.blankCost ?? ''} onChange={(e) => patchMap('pricing', 'cost', num(e.target.value))} />
                  </Field>
                  <Field label="Currency">
                    <input className={inputCls} value={draft.pricing?.currency || 'USD'} onChange={(e) => patchMap('pricing', 'currency', e.target.value)} />
                  </Field>
                </div>
              </Section>

              <Section title="Print area">
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Position">
                    <select className={inputCls} value={draft.printArea?.position || 'left-chest'} onChange={(e) => patchMap('printArea', 'position', e.target.value)}>
                      {PRINT_POSITIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </Field>
                  <Field label="Position Display">
                    <input className={inputCls} placeholder="Left Chest" value={draft.printArea?.positionDisplay || ''} onChange={(e) => patchMap('printArea', 'positionDisplay', e.target.value)} />
                  </Field>
                  <Field label="Width (inches)">
                    <input className={inputCls} type="number" step="0.1" value={draft.printArea?.width ?? ''} onChange={(e) => patchMap('printArea', 'width', num(e.target.value))} />
                  </Field>
                  <Field label="Height (inches)">
                    <input className={inputCls} type="number" step="0.1" value={draft.printArea?.height ?? ''} onChange={(e) => patchMap('printArea', 'height', num(e.target.value))} />
                  </Field>
                </div>
              </Section>

              <Section title="Decoration">
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Method">
                    <select className={inputCls} value={deco.method} onChange={(e) => patchMap('decoration', 'method', e.target.value)}>
                      {DECORATIONS.map((d) => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </Field>
                  <Field label="Lead Time (days)">
                    <input className={inputCls} type="number" value={deco.leadTime ?? ''} onChange={(e) => patchMap('decoration', 'leadTime', num(e.target.value))} />
                  </Field>
                </div>
                <div className="mt-2">
                  <Field label="Detail">
                    <input className={inputCls} placeholder="Full-color DTF transfer…" value={deco.detail} onChange={(e) => patchMap('decoration', 'detail', e.target.value)} />
                  </Field>
                </div>
              </Section>

              <Section title="Sizes">
                <div className="flex flex-wrap gap-1">
                  {[...ALL_SIZES, ...(draft.sizes || []).filter((s) => !(ALL_SIZES as readonly string[]).includes(s))].map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => toggleSize(s)}
                      className={`rounded-sm border px-2 py-0.5 text-[11px] ${(draft.sizes || []).includes(s) ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-bg text-muted'}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </Section>

              <Section title="Mockup">
                <Field label="Template File">
                  <input className={inputCls} placeholder="templates/hoodie-black-front.png" value={draft.mockup?.templateFile || ''} onChange={(e) => patchMap('mockup', 'templateFile', e.target.value)} />
                </Field>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <Field label="Logo X (px)">
                    <input className={inputCls} type="number" value={draft.mockup?.logoX ?? ''} onChange={(e) => patchMap('mockup', 'logoX', num(e.target.value))} />
                  </Field>
                  <Field label="Logo Y (px)">
                    <input className={inputCls} type="number" value={draft.mockup?.logoY ?? ''} onChange={(e) => patchMap('mockup', 'logoY', num(e.target.value))} />
                  </Field>
                  <Field label="Logo Max W (px)">
                    <input className={inputCls} type="number" value={draft.mockup?.logoMaxWidth ?? ''} onChange={(e) => patchMap('mockup', 'logoMaxWidth', num(e.target.value))} />
                  </Field>
                  <Field label="Logo Max H (px)">
                    <input className={inputCls} type="number" value={draft.mockup?.logoMaxHeight ?? ''} onChange={(e) => patchMap('mockup', 'logoMaxHeight', num(e.target.value))} />
                  </Field>
                </div>
              </Section>

              <Section title="Shopify">
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Product Type">
                    <input className={inputCls} placeholder="Hoodie" value={draft.shopify?.productType || ''} onChange={(e) => patchMap('shopify', 'productType', e.target.value)} />
                  </Field>
                  <Field label="Vendor">
                    <input className={inputCls} placeholder="Ghost Fit Apparel" value={draft.shopify?.vendor || ''} onChange={(e) => patchMap('shopify', 'vendor', e.target.value)} />
                  </Field>
                </div>
                <div className="mt-2 space-y-2">
                  <Field label="Tags (comma-separated)">
                    <input className={inputCls} placeholder="team-store, hoodie, black" value={tagsText} onChange={(e) => setTagsText(e.target.value)} />
                  </Field>
                  <Field label="Size Chart URL">
                    <input className={inputCls} placeholder="https://…" value={draft.shopify?.sizeChartUrl || ''} onChange={(e) => patchMap('shopify', 'sizeChartUrl', e.target.value)} />
                  </Field>
                </div>
              </Section>

              <div className="sticky bottom-0 -mx-1 flex gap-2 border-t border-divider bg-surface px-1 py-3">
                <Button size="sm" disabled={!styleOk || saving} onClick={save}>{saving ? 'Saving…' : 'Save'}</Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={saving}
                  onClick={() => { setDraft(product); setTagsText((product?.shopify?.tags || []).join(', ')); setEditing(false); }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div>
              <Row label="ID" value={<span className="font-mono text-[10px]">{draft.id}</span>} />
              <Row label="Style Number" value={draft.baseProduct?.styleNumber} />
              <Row label="Brand Partner" value={draft.brandPartner} />
              <Row label="Category" value={draft.category} />
              <Row label="Gender" value={draft.gender} />
              <Row label="Decoration" value={draft.decoration?.method || draft.decorationMethod} />
              <Row label="Tier" value={draft.tier} />
              <Row label="Color" value={draft.baseProduct?.color} />
              <Row label="Price" value={draft.pricing?.basePrice != null ? `$${draft.pricing.basePrice}` : undefined} />
              <Row label="Print Area" value={draft.printArea?.position ? `${draft.printArea.position}${draft.printArea.width ? ` · ${draft.printArea.width}″×${draft.printArea.height}″` : ''}` : undefined} />
              <Row label="Sizes" value={(draft.sizes || []).length ? (draft.sizes || []).join(', ') : undefined} />
              <Row label="Sort Order" value={draft.sortOrder} />
              <Row label="Active" value={draft.active !== false ? 'yes' : <span className="text-warning">no</span>} />
              <Section
                title="Visual placement"
                badge={
                  <Button size="sm" variant="secondary" onClick={() => setVisualOpen(true)}>
                    <Frame className="h-3.5 w-3.5" aria-hidden /> Edit zones
                  </Button>
                }
              >
                <Row
                  label="Print areas"
                  value={(draft.printAreas || []).length
                    ? (draft.printAreas || []).map((p) => p.label || p.position || p.id).join(', ')
                    : undefined}
                />
                <Row
                  label="Zones placed"
                  value={VIEW_KEYS
                    .map((v) => ({ v, n: (draft.views?.[v]?.areas || []).length }))
                    .filter(({ n }) => n > 0)
                    .map(({ v, n }) => `${v} ${n}`)
                    .join(' · ') || undefined}
                />
              </Section>
              <div className="mt-4 flex flex-wrap gap-2 border-t border-divider pt-3">
                <Button size="sm" variant="secondary" disabled={busy} onClick={toggleApprove}>
                  {draft.approved ? 'Unapprove' : 'Approve'}
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={busy}
                  onClick={() => {
                    // The copy is a different product — never inherit the
                    // original's Shopify base-product / inventory linkage.
                    const { baseProductCreated, baseProductCreatedAt, masterInventoryKey, shopifyProductId, ...baseRest } = draft.baseProduct || {};
                    void baseProductCreated; void baseProductCreatedAt; void masterInventoryKey; void shopifyProductId;
                    onDuplicate?.({ ...draft, id: '', displayName: `${draft.displayName || draft.id} (copy)`, baseProduct: baseRest });
                  }}
                >
                  Duplicate
                </Button>
                {confirmDelete ? (
                  <>
                    <Button size="sm" variant="ghost" disabled={busy} onClick={doDelete}>Confirm delete</Button>
                    <Button size="sm" variant="ghost" disabled={busy} onClick={() => setConfirmDelete(false)}>Cancel</Button>
                  </>
                ) : (
                  <Button size="sm" variant="ghost" disabled={busy} onClick={() => setConfirmDelete(true)}>Delete</Button>
                )}
              </div>

              {/* ---- Product 360: linkage view (GET /api/products/:id/links) ---- */}
              {linksKey && (
                <div className="mt-4 space-y-4">
                  {!links && !linksError && <p className="text-[11px] text-muted">Loading linkage…</p>}
                  {linksError != null && (
                    <p className="text-[11px] text-red-600">{"Couldn't load linkage — not showing live data."}</p>
                  )}
                  {links && (
                    <>
                      <Section title="Personalization" badge={persBadge(links.personalization)}>
                        <Row label="Zones" value={links.personalization.zoneCount} />
                        <Row
                          label="Templates"
                          value={links.personalization.templateIds.length
                            ? <span className="font-mono text-[10px]">{links.personalization.templateIds.join(', ')}</span>
                            : undefined}
                        />
                        {links.personalization.lastSyncedAt && (
                          <Row label="Last synced" value={new Date(links.personalization.lastSyncedAt).toLocaleString()} />
                        )}
                        <Link to="/personalization/assign" onClick={onClose} className="mt-2 inline-block text-[11px] underline">
                          Edit zones →
                        </Link>
                      </Section>

                      <Section title="Store templates">
                        {links.storeTemplates.length === 0 ? (
                          <p className="text-[11px] text-muted">Not in any store template yet — add the garment in Product lineups, then bind designs in Assign to Products.</p>
                        ) : (
                          <div className="space-y-2">
                            {links.storeTemplates.map((t) => (
                              <div key={t.id} className="rounded-md border border-divider p-2">
                                <div className="flex items-center gap-2">
                                  <span className="min-w-0 truncate text-[12px] font-medium">{t.name}</span>
                                  {t.sport && <Badge className="bg-primary/10 text-primary">{t.sport}</Badge>}
                                  {t.active
                                    ? <Badge className="bg-success/10 text-success">active</Badge>
                                    : <Badge className="bg-bg text-muted">inactive</Badge>}
                                </div>
                                {(t.styleEntry.designs || []).length > 0 ? (
                                  <ul className="mt-1 space-y-0.5">
                                    {(t.styleEntry.designs || []).map((d, i) => (
                                      <li key={i} className="font-mono text-[10px] text-muted">
                                        {d.designTemplateId} @ {d.position}
                                      </li>
                                    ))}
                                  </ul>
                                ) : (
                                  <p className="mt-1 text-[10px] text-subtle">No recipe designs — uses the logo flow.</p>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                        <div className="mt-2 flex flex-wrap gap-3">
                          {links.storeTemplates.map((t) => (
                            <Link
                              key={`open-${t.id}`}
                              to={storeTemplateHref(t.id, t.styleEntry.styleNumber, { kind: t.kind })}
                              onClick={onClose}
                              className="text-[11px] underline"
                            >
                              Open {t.name} →
                            </Link>
                          ))}
                          <Link
                            to={templateEditorHref({ styleNumber: String(draft.baseProduct?.styleNumber || links.storeTemplates[0]?.styleEntry.styleNumber || '') })}
                            onClick={onClose}
                            className="text-[11px] underline"
                          >
                            {links.storeTemplates.length ? 'Assign more artwork →' : 'Connect this style to a sport template →'}
                          </Link>
                        </div>
                      </Section>

                      <Section
                        title="Base product / inventory"
                        badge={links.baseProduct?.baseProductCreated
                          ? <Badge className="bg-success/10 text-success">Linked</Badge>
                          : <Badge className="bg-bg text-muted">No base product</Badge>}
                      >
                        {links.baseProduct?.baseProductCreated ? (
                          <>
                            <Row
                              label="Shopify Product"
                              value={links.baseProduct.shopifyProductId
                                ? <span className="font-mono text-[10px]">{String(links.baseProduct.shopifyProductId)}</span>
                                : undefined}
                            />
                            <Row
                              label="Inventory Key"
                              value={links.baseProduct.masterInventoryKey
                                ? <span className="font-mono text-[10px]">{String(links.baseProduct.masterInventoryKey)}</span>
                                : undefined}
                            />
                            <Row
                              label="Master Inventory"
                              value={links.masterInventoryExists
                                ? <span className="text-success">found</span>
                                : <span className="text-red-600">missing</span>}
                            />
                            <Button size="sm" variant="secondary" className="mt-2" disabled={linkBusy} onClick={refreshBaseStatus}>
                              {linkBusy ? 'Working…' : 'Refresh status'}
                            </Button>
                          </>
                        ) : (
                          <>
                            <p className="mb-2 text-[11px] text-muted">
                              No base product yet — this config has no Shopify inventory tracking.
                            </p>
                            <Button size="sm" variant="secondary" disabled={linkBusy} onClick={createBaseProduct}>
                              {linkBusy ? 'Working…' : 'Create base product'}
                            </Button>
                          </>
                        )}
                      </Section>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </SheetContent>

      <ProductVisualEditor
        open={visualOpen}
        onClose={() => setVisualOpen(false)}
        product={draft}
        onSaved={(next) => setDraft(next)}
      />
    </Sheet>
  );
}
